import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/auth/AuthProvider'
import { FxAlert, FxButton, FxIcon, FxPanel } from '@/components/fx'
import { PlanQuota } from '@/components/PlanQuota'
import {
  DEFAULT_MAX_UPLOAD_MB,
  UPLOAD_ACCEPT,
  UPLOAD_EXTENSIONS,
} from '@/components/upload/uploadQueue'
import { configApi } from '@/lib/api'
import { useUsage } from '@/hooks/useUsage'
import { FunnelLayout } from '@/layouts/FunnelLayout'
import { pendingUpload } from '@/lib/pendingUpload'
import { cn } from '@/lib/utils'
import type { DetectionMethod } from '@/types/domain'

/**
 * Step one of the funnel: choose the form, before signing in.
 *
 * The file is stashed in IndexedDB rather than uploaded, because an upload
 * needs a session and demanding the account first is what loses people here.
 * Whoever is already signed in skips straight to the analysis step.
 */

/**
 * What the gateway accepts, and how much of it — from `components/upload`,
 * which reads both off the handler. They used to be a second copy here, and
 * two copies of a server limit drift the first time the server's changes.
 */
const ACCEPT = UPLOAD_ACCEPT
const ACCEPTED_EXTENSIONS = UPLOAD_EXTENSIONS.map((extension) => `.${extension}`)
const MAX_MB = DEFAULT_MAX_UPLOAD_MB

/**
 * The model reads the form; the geometric detector is the safety net.
 *
 * `llm` (Gemini on Vertex, in the EU) understands a field that has no printed
 * border and gives it a sensible name and type — the geometric detector is
 * blind to exactly those, so a form laid out by it arrives half-read and the
 * person spends their first minutes drawing boxes.
 *
 * It used to be fixed to `cv` for a good reason: the model path needs a key,
 * a project and a region, and any of them missing left the visitor with an
 * empty template — the worst possible first minute. That is now handled where
 * it belongs, in the gateway: a model run that errors or finds nothing falls
 * back to the geometric detector by itself and says so in `fellBackFrom`. So
 * asking for the better detector can no longer fail closed.
 */
const DETECT_METHOD: DetectionMethod = 'llm'

/**
 * What the data-protection card says, and why it no longer names a model.
 *
 * The funnel lays the fields out geometrically (`DETECT_METHOD` above) and
 * the reading is Vision plus the checkbox and signature models. The page pass
 * — the one step where a language model would see the document — is gated on
 * the worker's `LLM_PASS_ENABLED` **and** an `llmConsent` record on the
 * template, and neither exists on this path: the template is created by the
 * analysis step and nothing writes a consent onto it. The card therefore says
 * what runs today. A consent statement that is not persisted is worse than no
 * statement, and the model reading is what the paid "Präzision" option is —
 * it belongs to the screen that sells it, with the consent record that goes
 * with it.
 */

/**
 * How the vendor is named to a person. The server says "google"; nobody
 * reading a consent sentence calls it that, and the region matters to them
 * more than the model does.
 */
function vendorName(ai?: { provider?: string; region?: string }): string {
  if (!ai?.provider) return 'ein KI-Modell'
  const named = ai.provider === 'google' ? 'Google Gemini' : ai.provider === 'anthropic' ? 'Anthropic Claude' : ai.provider
  return ai.region === 'eu' ? `${named} (EU)` : named
}

const env = import.meta.env as Record<string, string | undefined>

/**
 * The filled example, for someone with no form to hand. It is a real file
 * fed through the same path as a dropped one, not a special case — a demo
 * that takes a different route through the code is a demo of something else.
 *
 * Unset in a build with no sample asset, and then the tile is not drawn at
 * all: a button that cannot do what it says is worse than its absence.
 */
const SAMPLE_URL = env.VITE_SAMPLE_FORM_URL ?? ''
const SAMPLE_THUMBNAIL = env.VITE_SAMPLE_FORM_THUMBNAIL ?? ''

/** Why this file cannot be used, or null when it can. */
export function fileRefusal(file: File, maxMb = MAX_MB): string | null {
  const name = file.name.toLowerCase()
  if (!ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    return 'Wir können PDF, JPG und PNG lesen — diese Datei leider nicht.'
  }
  if (file.size > maxMb * 1024 * 1024) {
    return `Die Datei ist größer als ${maxMb} MB. Ein Foto der Seite reicht völlig.`
  }
  return null
}

/**
 * The "no pages left" warning, and nothing else — `PlanQuota` also renders a
 * running figure, which the funnel header already carries.
 */
function ExhaustedPages() {
  const { signedIn } = useAuth()
  const usage = useUsage()
  if (!signedIn) return null
  if (usage.data?.usage?.pagesRemaining !== 0) return null
  return <PlanQuota voice="du" />
}

/** Same for the template limit, which is what a free account hits first. */
function TemplateLimit() {
  const { signedIn } = useAuth()
  if (!signedIn) return null
  return <PlanQuota kind="templates" voice="du" />
}

/** The one line a signed-out visitor needs before picking a file. */
function SignInHint() {
  const { signedIn, loading } = useAuth()
  if (signedIn || loading) return null
  return (
    <p className="text-small text-text-muted">
      Nach der Auswahl legst du ein Konto an — deine Datei bleibt dabei erhalten.
    </p>
  )
}

export function PickFormPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [over, setOver] = useState(false)
  const [loadingSample, setLoadingSample] = useState(false)
  // Dragging over a child element fires dragleave on the parent, so the
  // highlight flickers unless entries and leaves are counted rather than
  // toggled.
  const dragDepth = useRef(0)

  // What actually reads the document, from the server rather than from a
  // constant here: this sentence is a data-protection statement, and a
  // hard-coded vendor name is one deployment change away from being false.
  const config = useQuery({ queryKey: ['config'], queryFn: () => configApi.get() })
  const aiAvailable = config.data?.ai.available === true
  const aiVendor = vendorName(config.data?.ai)

  async function choose(file: File) {
    const refusal = fileRefusal(file)
    if (refusal) {
      setError(refusal)
      return
    }
    setError(null)
    await pendingUpload.save(file, DETECT_METHOD, aiAvailable)
    // Always aim at the analysis step, which sits behind the gate: a
    // signed-out visitor is sent to sign in and bounced back here, and a
    // signed-in one goes straight through. Deciding between the two right
    // here would be wrong while the bootstrap refresh is still in flight —
    // a returning person who picks a file quickly would be sent to a login
    // screen they do not need.
    navigate('/onboarding/analyse')
  }

  async function chooseSample() {
    setError(null)
    setLoadingSample(true)
    try {
      const response = await fetch(SAMPLE_URL)
      if (!response.ok) throw new Error(String(response.status))
      const blob = await response.blob()
      const filename = SAMPLE_URL.split('/').pop() || 'beispiel.jpg'
      await choose(new File([blob], filename, { type: blob.type }))
    } catch {
      setError('Das Beispiel lässt sich gerade nicht laden. Lade solange dein eigenes Formular hoch.')
    } finally {
      setLoadingSample(false)
    }
  }

  return (
    <FunnelLayout step="upload">
      <main className="mx-auto grid w-full max-w-[1200px] gap-10 px-5 py-8 sm:px-10 lg:grid-cols-[minmax(0,1fr)_380px] lg:px-[120px] lg:py-12">
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-tile-title xs:text-page-title">Dein Formular testen</h1>
            <p className="mt-2">
              Lade ein <b>ausgefülltes</b> Formular hoch – wir finden die Felder und lesen es
              sofort aus.
            </p>
          </div>

          {error && <FxAlert tone="danger">{error}</FxAlert>}

          {/* Both render nothing unless the account has actually run out.
              The running page figure is in the header, so only the exhausted
              case is repeated here — and that one is worth repeating, since
              otherwise the visitor learns it after signing in, at step two. */}
          <TemplateLimit />
          <ExhaustedPages />

          {/* The focusable element is the `sr-only` input, which is clipped to
              a pixel — so the ring has to be drawn on the control the person
              can see, or tabbing here shows nothing at all. */}
          <label
            className={cn(
              'flex min-h-[280px] cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border-[1.5px] border-dashed border-primary p-8 text-center sm:min-h-[360px]',
              'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary-strong',
              over ? 'bg-primary-tonal' : 'bg-primary-subtle',
            )}
            onDragEnter={() => {
              dragDepth.current += 1
              setOver(true)
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => {
              dragDepth.current -= 1
              if (dragDepth.current <= 0) setOver(false)
            }}
            onDrop={(event) => {
              event.preventDefault()
              dragDepth.current = 0
              setOver(false)
              const file = event.dataTransfer.files[0]
              if (file) void choose(file)
            }}
          >
            <input
              type="file"
              aria-label="Formular auswählen"
              accept={ACCEPT}
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) void choose(file)
                // Picking the same file twice in a row fires no change event
                // unless the value is cleared — which happens whenever the
                // first attempt was refused for its size.
                event.target.value = ''
              }}
            />
            <FxIcon name="assignment_add" size={110} className="text-primary-strong" />
            <span className="mt-2 text-tile-title">Formular hierher ziehen</span>
            <span className="text-primary-text underline">oder eine Datei auswählen</span>
            <span className="mt-3 text-small">PDF, JPG oder PNG · bis {MAX_MB} MB</span>
          </label>

          {/* The next step sits behind the gate, so a signed-out visitor is
              about to land on a sign-in screen. Saying so beforehand — and
              that the file survives it — is the difference between a step and
              a surprise. */}
          <SignInHint />

          <div className="flex items-start gap-4 rounded-sm bg-surface p-4 shadow-raised sm:px-5">
            <FxIcon name="lock" size={24} className="mt-1 flex-none text-primary-strong" />
            <p className="text-small leading-relaxed">
              Dein Dokument wird nur für dieses Formular ausgewertet.{' '}
              {aiAvailable ? (
                <>
                  Die Felder erkennt und liest <b>{aiVendor}</b>, für die Texterkennung nutzen wir{' '}
                  <b>Google Cloud Vision</b>. Mit dem Hochladen stimmst du dieser Verarbeitung zu.
                </>
              ) : (
                <>
                  Für die Texterkennung nutzen wir <b>Google Cloud Vision</b>; ein Sprachmodell liest
                  dein Dokument dabei nicht.
                </>
              )}{' '}
              Nichts davon wird zum Trainieren verwendet.
            </p>
          </div>
        </div>

        <aside className="flex flex-col gap-6 lg:pt-24">
          {SAMPLE_URL && (
            <FxPanel className="flex flex-col gap-4 p-6">
              <h2 className="text-panel-title">Kein Formular zur Hand?</h2>
              <p className="text-small">
                Teste mit unserem ausgefüllten Beispiel – einem Gesundheitsfragebogen.
              </p>
              {SAMPLE_THUMBNAIL && (
                <img
                  src={SAMPLE_THUMBNAIL}
                  alt="Ausgefülltes Beispielformular"
                  className="w-40 bg-surface shadow-raised"
                />
              )}
              <FxButton variant="tonal" onClick={() => void chooseSample()} disabled={loadingSample}>
                {loadingSample ? 'Beispiel wird geladen …' : 'Mit dem Beispiel testen'}
              </FxButton>
            </FxPanel>
          )}

          <section className="flex flex-col gap-3 text-small">
            <h2 className="text-body-strong">Tipps für ein Foto</h2>
            {[
              { icon: 'crop_free', text: 'Das ganze Blatt im Bild' },
              { icon: 'photo_camera', text: 'Gerade von oben aufnehmen' },
              { icon: 'light_mode', text: 'Gleichmäßiges Licht, kein Schatten' },
            ].map((tip) => (
              <p key={tip.icon} className="flex items-center gap-3">
                <FxIcon name={tip.icon} size={20} className="flex-none text-primary-strong" />
                {tip.text}
              </p>
            ))}
          </section>
        </aside>
      </main>
    </FunnelLayout>
  )
}
